import { CustomPropertyViewModelDecorator, EnumDecorator, EnumMetaData, EnumPropertyViewModel, EnumPropertyViewModelItem, MessageResourceManager, NumberDecorator, NumericMetaData, NumericPropertyViewModel, PropertyViewModelFactory } from '@nts/std';
import { GeneratedSignerLayoutCollectionItemViewModel } from '../generated/view-models/generated-signer-layout.collection-item-view-model';
import { Subject, debounceTime, merge, takeUntil, throttleTime } from 'rxjs';
import { TreeNode } from 'primeng/api';
import { UndoService } from '../services/undo.service';
import { SignTemplateLongOpOrchestratorViewModel } from './sign-template-long-op.orchestrator-view-model';
import { RectInterface } from 'src/app/shared/components/custom-pdf-editor/custom-pdf-editor.component';

export enum PageOrientationType {
    Vertical = 1,
    Horizontal = 2
}

export enum PageFormatType {
    A4 = 1, // (210x297mm)
    A3 = 2, // (297x420mm)
    A5 = 3, // (148x210mm)
    Custom = 99
}

export class SignerLayoutCollectionItemViewModel extends GeneratedSignerLayoutCollectionItemViewModel implements TreeNode, RectInterface {
    left: number = 0;
    width: number = 0;
    top: number = 0;
    height: number = 0;
    isSelected: boolean = false;
    touched = false;
    isValid = false;

    undoService: UndoService<any>;
    updateStateDebouncer = new Subject<void>();
    undoThrottle = new Subject<void>();
    redoThrottle = new Subject<void>();
    isDragging: boolean = false;

    // tslint:disable-next-line: member-ordering variable-name
    private _pageFormatType: EnumPropertyViewModel;



    @CustomPropertyViewModelDecorator()
    @EnumDecorator({ displayNameKey: 'PageFormatType', })
    public get pageFormatType(): EnumPropertyViewModel {
        if (this._pageFormatType == null) {
            const init = PropertyViewModelFactory.createPVMInitializationInfo<EnumMetaData>(
                this, 'pageFormatType', null, this, this.modifiedSubscriber, this.eventDispatcher, null, false, false
            );
            // Default value
            init.customValue = PageFormatType.A4;
            this._pageFormatType = new EnumPropertyViewModel(init);

            this._pageFormatType.valueDescriptions = [
                new EnumPropertyViewModelItem(PageFormatType.A3,
                    MessageResourceManager.Current.getMessage('PageFormatType_A3')),

                new EnumPropertyViewModelItem(PageFormatType.A4,
                    MessageResourceManager.Current.getMessage('PageFormatType_A4')),

                new EnumPropertyViewModelItem(PageFormatType.A5,
                    MessageResourceManager.Current.getMessage('PageFormatType_A5')),

                new EnumPropertyViewModelItem(PageFormatType.Custom,
                    MessageResourceManager.Current.getMessage('PageFormatType_Custom'))
            ]
        }
        return this._pageFormatType;
    }

    // tslint:disable-next-line: member-ordering variable-name
    private _pageOrientationType: EnumPropertyViewModel;

    @CustomPropertyViewModelDecorator()
    @EnumDecorator({ displayNameKey: 'PageOrientationType', })
    public get pageOrientationType(): EnumPropertyViewModel {
        if (this._pageOrientationType == null) {
            const init = PropertyViewModelFactory.createPVMInitializationInfo<EnumMetaData>(
                this, 'pageOrientationType', null, this, this.modifiedSubscriber, this.eventDispatcher, null, false, false
            );
            // Default value
            init.customValue = PageOrientationType.Vertical;
            this._pageOrientationType = new EnumPropertyViewModel(init);

            this._pageOrientationType.valueDescriptions = [
                new EnumPropertyViewModelItem(PageOrientationType.Vertical,
                    MessageResourceManager.Current.getMessage('PageOrientationType_Vertical')),

                new EnumPropertyViewModelItem(PageOrientationType.Horizontal,
                    MessageResourceManager.Current.getMessage('PageOrientationType_Horizontal'))
            ]
        }
        return this._pageOrientationType;
    }

    // tslint:disable-next-line: member-ordering variable-name
    private _customPageWidth: NumericPropertyViewModel;

    @CustomPropertyViewModelDecorator()
    @NumberDecorator({ displayNameKey: 'SignerLayout_CustomPageWidth_DisplayName' })
    public get customPageWidth(): NumericPropertyViewModel {
        if (this._customPageWidth == null) {
            const init = PropertyViewModelFactory.createPVMInitializationInfo<NumericMetaData>(
                this, 'customPageWidth', null, this, this.modifiedSubscriber, this.eventDispatcher, null, false, false
            );
            init.customValue = 210;
            this._customPageWidth = new NumericPropertyViewModel(init);
            this._customPageWidth.isEnabled = false;
        }
        return this._customPageWidth;
    }

    // tslint:disable-next-line: member-ordering variable-name
    private _leftCoordinateInMM: NumericPropertyViewModel;

    @CustomPropertyViewModelDecorator()
    @NumberDecorator({ displayNameKey: 'SignerLayout_LeftCoordinateInMM_DisplayName' })
    public get leftCoordinateInMM(): NumericPropertyViewModel {
        if (this._leftCoordinateInMM == null) {
            const init = PropertyViewModelFactory.createPVMInitializationInfo<NumericMetaData>(
                this, 'leftCoordinateInMM', null, this, this.modifiedSubscriber, this.eventDispatcher, null, false, false
            );
            this._leftCoordinateInMM = new NumericPropertyViewModel(init);
        }
        return this._leftCoordinateInMM;
    }

    // tslint:disable-next-line: member-ordering variable-name
    private _topCoordinateInMM: NumericPropertyViewModel;

    @CustomPropertyViewModelDecorator()
    @NumberDecorator({ displayNameKey: 'SignerLayout_TopCoordinateInMM_DisplayName' })
    public get topCoordinateInMM(): NumericPropertyViewModel {
        if (this._topCoordinateInMM == null) {
            const init = PropertyViewModelFactory.createPVMInitializationInfo<NumericMetaData>(
                this, 'topCoordinateInMM', null, this, this.modifiedSubscriber, this.eventDispatcher, null, false, false
            );
            this._topCoordinateInMM = new NumericPropertyViewModel(init);
        }
        return this._topCoordinateInMM;
    }

    // tslint:disable-next-line: member-ordering variable-name
    private _customPageHeight: NumericPropertyViewModel;

    @CustomPropertyViewModelDecorator()
    @NumberDecorator({ displayNameKey: 'SignerLayout_CustomPageHeight_DisplayName' })
    public get customPageHeight(): NumericPropertyViewModel {
        if (this._customPageHeight == null) {
            const init = PropertyViewModelFactory.createPVMInitializationInfo<NumericMetaData>(
                this, 'customPageHeight', null, this, this.modifiedSubscriber, this.eventDispatcher, null, false, false
            );
            init.customValue = 297;
            this._customPageHeight = new NumericPropertyViewModel(init);
            this._customPageHeight.isEnabled = false;
        }
        return this._customPageHeight;
    }

    async undo() {
        this.undoThrottle.next();
    }

    async redo() {
        this.redoThrottle.next();
    }

    resetRectangleCoordinates() {
        this.topCoordinate.value = null;
        this.leftCoordinate.value = null;
    }

    private getCurrentState() {
        return {
            customPageHeight: this.customPageHeight.value,
            customPageWidth: this.customPageWidth.value,
            specificPage: this.specificPage.value,
            lastPage: this.lastPage.value,
            pageFormatType: this.pageFormatType.value,
            pageOrientationType: this.pageOrientationType.value,
            leftCoordinate: this.leftCoordinate.value,
            topCoordinate: this.topCoordinate.value,
        }
    }

    private async setCurrentState(value) {

        await this.customPageHeight.setValueAsync(value.customPageHeight);
        await this.customPageWidth.setValueAsync(value.customPageWidth);
        await this.lastPage.setValueAsync(value.lastPage);
        await this.specificPage.setValueAsync(value.specificPage);
        await this.pageFormatType.setValueAsync(value.pageFormatType);
        await this.pageOrientationType.setValueAsync(value.pageOrientationType);
        await this.leftCoordinate.setValueAsync(value.leftCoordinate);
        await this.topCoordinate.setValueAsync(value.topCoordinate);
    }

    private oldSpecificPage: number = null;

    override async postInit() {
        await super.postInit();

        this.specificPage.minValue = 1;
        // this.specificPage.minValueValidation = 2;
        // this.specificPage.maxValueValidation = 7;
        // this.specificPage.maxValue = 8;

        this.undoService = new UndoService();

        this.specificPage.propertyChanged.pipe(takeUntil(this.destroySubscribers$)).subscribe(async () => {
            if (this.specificPage.value > 0) {
                const ovm = this.externalRetriever as SignTemplateLongOpOrchestratorViewModel;
                // ritorna true se si vuole variare o se non ci sono altre pagine in conflitto
                const result = await ovm.checkFormatAndOrientationForSpecificPage(this);
                if (!result) {
                    const newValue = this.undoService.getCurrentValue();
                    await this.setCurrentState(newValue);
                }
                this.updateStateDebouncer.next();
            } else {
                if (this.specificPage.isEnabled) {
                    setTimeout(() => this.specificPage.value = 1);
                }
                this.updateStateDebouncer.next();
            }
        })

        merge(
            this.leftCoordinate.propertyChanged,
            this.topCoordinate.propertyChanged
        ).pipe(takeUntil(this.destroySubscribers$)).subscribe(() => {
            this.updateStateDebouncer.next();
        })

        this.lastPage.propertyChanged.pipe(takeUntil(this.destroySubscribers$)).subscribe(async () => {
            if (this.lastPage.value === true) {
                const ovm = this.externalRetriever as SignTemplateLongOpOrchestratorViewModel;
                // ritorna true se si vuole variare o se non ci sono altre pagine in conflitto
                const result = await ovm.checkFormatAndOrientationForLastPage(this);
                if (!result) {
                    const newValue = this.undoService.getCurrentValue();
                    await this.setCurrentState(newValue);
                }
                this.specificPage.isRequired = false;
                this.updateStateDebouncer.next();
            } else {
                this.specificPage.isRequired = true;
                this.updateStateDebouncer.next();
            }

            this.specificPage.isEnabled = this.lastPage.value == null || this.lastPage.value === false;
            if (this.specificPage.isEnabled == false) {
                this.oldSpecificPage = this.specificPage.value
                this.specificPage.value = null;

            } else {

                if (this.oldSpecificPage != null) {
                    this.specificPage.value = this.oldSpecificPage;
                } else {
                    this.specificPage.value = null
                }
            }
            this.specificPage.validate();

        })

        this.specificPage.isEnabled = this.lastPage.value == null || this.lastPage.value === false;

        this.pageFormatType.propertyChanged.pipe(takeUntil(this.destroySubscribers$)).subscribe(async () => {
            if (!this.externalRetriever) {
                return;
            }

            if (this.specificPage.value > 0) {
                const ovm = this.externalRetriever as SignTemplateLongOpOrchestratorViewModel;
                // ritorna true se si vuole variare o se non ci sono altre pagine in conflitto
                const result = await ovm.checkFormatAndOrientationForSpecificPage(this);
                if (!result) {
                    const newValue = this.undoService.getCurrentValue();
                    await this.setCurrentState(newValue);
                }
                this.updateStateDebouncer.next();
            } else if (this.lastPage.value === true) {
                const ovm = this.externalRetriever as SignTemplateLongOpOrchestratorViewModel;
                // ritorna true se si vuole variare o se non ci sono altre pagine in conflitto
                const result = await ovm.checkFormatAndOrientationForLastPage(this);
                if (!result) {
                    const newValue = this.undoService.getCurrentValue();
                    await this.setCurrentState(newValue);
                }
                this.updateStateDebouncer.next();
            } else {
                this.updateStateDebouncer.next();
            }

            this.resetRectangleCoordinates();
            this.customPageHeight.isEnabled = this.pageFormatType.value === PageFormatType.Custom;
            this.customPageWidth.isEnabled = this.pageFormatType.value === PageFormatType.Custom;

        })

        merge(
            this.customPageHeight.propertyChanged,
            this.customPageWidth.propertyChanged,
            this.pageOrientationType.propertyChanged
        ).pipe(takeUntil(this.destroySubscribers$)).pipe(debounceTime(10)).subscribe(async () => {

            if (this.specificPage.value > 0) {
                const ovm = this.externalRetriever as SignTemplateLongOpOrchestratorViewModel;
                // ritorna true se si vuole variare o se non ci sono altre pagine in conflitto
                const result = await ovm.checkFormatAndOrientationForSpecificPage(this);
                if (!result) {
                    const newValue = this.undoService.getCurrentValue();
                    await this.setCurrentState(newValue);
                }
                this.updateStateDebouncer.next();
            } else if (this.lastPage.value === true) {
                const ovm = this.externalRetriever as SignTemplateLongOpOrchestratorViewModel;
                // ritorna true se si vuole variare o se non ci sono altre pagine in conflitto
                const result = await ovm.checkFormatAndOrientationForLastPage(this);
                if (!result) {
                    const newValue = this.undoService.getCurrentValue();
                    await this.setCurrentState(newValue);
                }
                this.updateStateDebouncer.next();
            } else {
                this.updateStateDebouncer.next();
            }

            this.resetRectangleCoordinates();
        })

        this.undoService.initialize(this.getCurrentState())

        // Aspetto 100 ms prima di aggiornare lo stato in modo da aspettare che prima scattino i comportamenti così salvo lo stato già con i comportamenti
        this.updateStateDebouncer.pipe(debounceTime(100)).subscribe(() => {
            this.undoService.update(this.getCurrentState());
        })

        // Mi assicuro che non ci siano click ad intermittenza più veloci di 500 ms
        this.undoThrottle.pipe(throttleTime(500)).subscribe(async () => {
            const newValue = this.undoService.undo();
            await this.setCurrentState(newValue);
        });
        this.redoThrottle.pipe(throttleTime(500)).subscribe(async () => {
            const newValue = this.undoService.redo();
            await this.setCurrentState(newValue);
        });
    }

    get type() {
        return 'leaf';
    }

    get label() {
        return `Firma ${(this.rowNumber.value) + 1}`;
    }

    get additionalInfo() {
        return `${this.lastPage.value === true ? 'Ultima pagina' : this.specificPage.value > 0 ? ('Pagina: ' + this.specificPage.value) : 'Pagina non impostata'}`;
    }

    get id(): number {
        return this.signerLayoutSerial.value;
    }
}
